import { getErrorFromUnknown } from '@/lib/errorUtils';
import { classNames } from '@/utility/className';
import React, {
  ComponentPropsWithoutRef,
  forwardRef,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useId,
  useState,
} from 'react';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useFormContext,
  UseFormReturn,
} from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import Alert from '../Alert';

import { showToast } from '../Alert/toast';
import Tooltip from '../Button/tooltip';
type InputProps = Omit<ComponentPropsWithoutRef<'input'>, 'name'> & {
  name: string;
};

const disabledFieldClasses =
  'disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500 disabled:shadow-none disabled:hover:border-gray-200';

export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(props, ref) {
    const isDateInput =
      props.type === 'date' || props.type === 'datetime-local';

    const dateSafeFocusClasses = classNames(
      'focus:outline-none focus-visible:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus:shadow-none focus-visible:shadow-none focus:border-gray-500 focus-visible:border-gray-500'
    );

    return (
      <input
        {...props}
        ref={ref}
        type={props.type}
        disabled={props.disabled}
        className={classNames(
          'border-default mt-1 block w-full rounded-sm border py-2 px-3 focus:outline-none sm:text-sm h-9 transition-shadow',
          isDateInput
            ? dateSafeFocusClasses
            : 'shadow-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500   ',
          disabledFieldClasses,
          props.className
        )}
      />
    );
  }
);

export function Label(props: ComponentPropsWithoutRef<'label'>) {
  return (
    <label
      {...props}
      className={classNames(
        'block text-sm font-medium text-gray-700',
        props.className
      )}
    >
      {props.children}
    </label>
  );
}

export function InputLeading(props: ComponentPropsWithoutRef<'div'>) {
  return (
    <span className="inline-flex flex-shrink-0 items-center rounded-l-sm border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
      {props.children}
    </span>
  );
}

type InputFieldProps = {
  label?: ReactNode;
  hint?: ReactNode;
  addOnFilled?: boolean;
  addOnSuffix?: ReactNode;
  hideErrorMessage?: boolean;
} & React.ComponentProps<typeof Input> & {
    labelProps?: React.ComponentProps<typeof Label>;
  };

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  function InputField(props, ref) {
    const generatedId = useId();
    const methods = useFormContext();
    const {
      label = props.name,
      labelProps,
      placeholder = props.name + '_placeholder' !== props.name + '_placeholder'
        ? props.name + '_placeholder'
        : '',
      className,

      addOnSuffix,
      addOnFilled = true,
      type,
      disabled,
      hint,
      hideErrorMessage = false,
      id: providedId,
      ...passThrough
    } = props;
    const inputId = providedId ?? generatedId;

    return (
      <div>
        {!!props.name && (
          <Label htmlFor={inputId} {...labelProps}>
            {label}
          </Label>
        )}
        {addOnSuffix ? (
          <div
            dir="ltr"
            className="group relative mb-1 flex items-center rounded-md  focus-within:outline-none "
          >
            <Input
              id={inputId}
              type={type}
              placeholder={placeholder}
              className={classNames(
                className,
                'disabled:bg-subtle disabled:hover:border-subtle disabled:cursor-not-allowed',
                '!my-0'
              )}
              {...passThrough}
              disabled={disabled}
              ref={ref}
            />
            {addOnSuffix && (
              <Addon
                isFilled={addOnFilled}
                className={classNames(' rounded-r-md')}
              >
                {addOnSuffix}
              </Addon>
            )}
          </div>
        ) : (
          <Input
            id={inputId}
            placeholder={placeholder}
            className={className}
            {...passThrough}
            disabled={disabled}
            ref={ref}
          />
        )}
        {hint && (
          <div className="text-gray mt-2 flex items-center text-sm text-gray-700">
            {hint}
          </div>
        )}
        {!hideErrorMessage &&
          methods?.formState?.errors[props.name]?.message && (
            <Alert
              className="mt-1"
              variant="error"
              message={methods.formState.errors[props.name]?.message as string}
            />
          )}
      </div>
    );
  }
);

type AddonProps = {
  children: React.ReactNode;
  isFilled?: boolean;
  className?: string;
  error?: boolean;
};

const Addon = ({ isFilled, children, className, error }: AddonProps) => (
  <div
    className={classNames(
      'addon-wrapper border-default [input:hover_+_&]:border-emphasis [input:hover_+_&]:border-l-default [&:has(+_input:hover)]:border-emphasis [&:has(+_input:hover)]:border-r-default h-9 border px-3',
      isFilled && 'bg-subtle',
      className
    )}
  >
    <div
      className={classNames(
        'min-h-9 flex flex-col justify-center text-sm leading-7',
        error ? 'text-error' : 'text-default'
      )}
    >
      <span className="flex whitespace-nowrap">{children}</span>
    </div>
  </div>
);

export const TextField = forwardRef<HTMLInputElement, InputFieldProps>(
  function TextField(props, ref) {
    return (
      <InputField
        ref={ref}
        {...props}
        className={classNames(props.className, 'text-black')}
      />
    );
  }
);

export const EmailInput = forwardRef<HTMLInputElement, InputFieldProps>(
  function EmailInput(props, ref) {
    return (
      <Input
        ref={ref}
        type="email"
        data-testid="email-field"
        autoCapitalize="none"
        autoComplete="email"
        autoCorrect="off"
        inputMode="email"
        disabled={props.disabled}
        {...props}
      />
    );
  }
);

export const EmailField = forwardRef<HTMLInputElement, InputFieldProps>(
  function EmailField(props, ref) {
    return (
      <InputField
        ref={ref}
        type="email"
        autoCapitalize="none"
        autoComplete="email"
        autoCorrect="off"
        inputMode="email"
        disabled={props.disabled}
        {...props}
      />
    );
  }
);

type TextAreaProps = Omit<ComponentPropsWithoutRef<'textarea'>, 'name'> & {
  name: string;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextAreaInput(props, ref) {
    return (
      <textarea
        ref={ref}
        {...props}
        className={classNames(
          'block w-full rounded-sm border border-gray-300 shadow-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-white sm:text-sm transition-shadow',
          disabledFieldClasses,
          props.className
        )}
      />
    );
  }
);

type TextAreaFieldProps = {
  label?: ReactNode;
} & React.ComponentProps<typeof TextArea> & {
    labelProps?: React.ComponentProps<typeof Label>;
    hideErrorMessage?: boolean;
  };

export const TextAreaField = forwardRef<
  HTMLTextAreaElement,
  TextAreaFieldProps
>(function TextField(props, ref) {
  const generatedId = useId();
  const methods = useFormContext();
  const {
    label = props.name,
    labelProps,
    hideErrorMessage = false,
    id: providedId,
    placeholder,
    ...passThrough
  } = props;
  const inputId = providedId ?? generatedId;
  const computedPlaceholder =
    placeholder ??
    (props.name + '_placeholder' !== props.name + '_placeholder'
      ? props.name + '_placeholder'
      : undefined);

  return (
    <div>
      {!!props.name && (
        <Label htmlFor={inputId} {...labelProps}>
          {label}
        </Label>
      )}
      <TextArea
        ref={ref}
        id={inputId}
        placeholder={computedPlaceholder || undefined}
        {...passThrough}
      />
      {!hideErrorMessage && methods?.formState?.errors[props.name]?.message && (
        <Alert
          className="mt-1"
          variant="error"
          message={methods.formState.errors[props.name]?.message as string}
        />
      )}
    </div>
  );
});

type FormProps<T extends object> = {
  form: UseFormReturn<T>;
  handleSubmit: SubmitHandler<T>;
} & Omit<ComponentPropsWithoutRef<'form'>, 'onSubmit'>;

const PlainForm = <T extends FieldValues>(
  props: FormProps<T>,
  ref: Ref<HTMLFormElement>
) => {
  const { form, handleSubmit, ...passThrough } = props;

  return (
    <FormProvider {...form}>
      <form
        ref={ref}
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();

          form
            .handleSubmit(handleSubmit)(event)
            .catch((err) => {
              showToast(`${getErrorFromUnknown(err).message}`, 'error');
            });
        }}
        {...passThrough}
      >
        {
          /* @see https://react-hook-form.com/advanced-usage/#SmartFormComponent */
          React.Children.map(props.children, (child) => {
            if (
              React.isValidElement<{ name?: string }>(child) &&
              child.props.name
            ) {
              return React.createElement(child.type, {
                ...child.props,
                register: form.register,
                key: child.props.name,
              });
            }
            return child;
          })
        }
      </form>
    </FormProvider>
  );
};

export const Form = forwardRef(PlainForm) as <T extends FieldValues>(
  p: FormProps<T> & { ref?: Ref<HTMLFormElement> }
) => ReactElement;

export function FieldsetLegend(props: ComponentPropsWithoutRef<'legend'>) {
  return (
    <legend
      {...props}
      className={classNames(
        'text-sm font-medium text-gray-700',
        props.className
      )}
    >
      {props.children}
    </legend>
  );
}

export function InputGroupBox(props: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={classNames(
        'space-y-2 rounded-sm border border-gray-300 bg-white p-2',
        props.className
      )}
    >
      {props.children}
    </div>
  );
}

export const PasswordField = forwardRef<HTMLInputElement, InputFieldProps>(
  function PasswordField(props, ref) {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const toggleIsPasswordVisible = useCallback(
      () => setIsPasswordVisible(!isPasswordVisible),
      [isPasswordVisible, setIsPasswordVisible]
    );
    const textLabel = isPasswordVisible ? 'Hide Password' : 'Show Password';
    return (
      <div className="[&_.group:hover_.addon-wrapper]:border-emphasis relative [&_.group:focus-within_.addon-wrapper]:border-neutral-300">
        <InputField
          type={isPasswordVisible ? 'text' : 'password'}
          placeholder={props.placeholder || '•••••••••••••'}
          ref={ref}
          {...props}
          className={classNames('border-r-0 pr-10', props.className)}
          addOnSuffix={
            <Tooltip content={textLabel}>
              <button
                className="text-sm "
                type="button"
                onClick={() => toggleIsPasswordVisible()}
              >
                {isPasswordVisible ? (
                  <FiEyeOff className="h-4 stroke-[2.5px]" />
                ) : (
                  <FiEye className="h-4 stroke-[2.5px]" />
                )}
                <span className="sr-only">{textLabel}</span>
              </button>
            </Tooltip>
          }
        />
      </div>
    );
  }
);
